import * as React from "react";
import {Button, Icon, Input, Loader, Segment} from "semantic-ui-react";
import ReactTable from "react-table";
import {formatDate} from "../../../../../../format";
import {RollbackConfigModal} from "../modals/RollbackConfigModal";
import {ConfigContext} from "../../../../context/ConfigContext";
import {CompanyConfigHistory, CompanyConfiguration, CompanyHistory} from "../../../../../../generated/api";
import {backend} from "../../../../../../xconvert-backend";
import {authentication} from "../../../../../../authentication";
import {HistoryModal} from "../modals/HistoryModal";
import {IfBox} from "../../../../../style/if";
import {workingConfig} from "../../ConfigSignal";

export interface HistoryTableProps {
    reloadFromContext: (reload: boolean) => void
}

export function HistoryTable(props: HistoryTableProps) {
    const context = React.useContext(ConfigContext);

    const [isLoading, setIsLoading] = React.useState(true);
    const [history, setHistory] = React.useState<CompanyHistory[] | null>(null);
    const [selectedHistory, setSelectedHistory] = React.useState<CompanyHistory | null>(null);
    const [followingConfig, setFollowingConfig] = React.useState<CompanyConfiguration | null>(null);
    const [showChanges, setShowChanges] = React.useState(false);
    const [showRollBackView, setShowRollBackView] = React.useState(false);
    const [commentQuery, setCommentQuery] = React.useState<string>("");

    // Table
    const [take, setTake] = React.useState(10);
    const [page, setPage] = React.useState(1);
    const [sortBy, setSortBy] = React.useState<"TIMESTAMP" | "CHANGED_BY" | "COMMENT">("TIMESTAMP");
    const [sortDirection, setSortDirection] = React.useState<"DESC" | "ASC">("DESC");
    const [count, setCount] = React.useState(-1);

    React.useEffect(() => {
        fetchHistory().then();
    }, []);

    async function fetchHistory(takeParam = take, pageParam = page, cQuery = commentQuery) {
        setIsLoading(true);
        setTake(takeParam);
        setPage(pageParam);
        const auth = backend.withTokenAuthHeader(authentication.token);
        const historyData: CompanyConfigHistory = await backend.internalApi.fetchCompanyConfigurationHistory(
            context.companyId,
            cQuery,
            takeParam,
            (pageParam - 1) * takeParam,
            sortBy,
            sortDirection,
            null,
            'CONFIG_ONLY',
            auth
        );
        setHistory(historyData.results);
        setCount(historyData.count);
        context.setHistory(historyData);
        setIsLoading(false);
    }

    function reset() {
        setHistory(null);
        setCount(-1);
        setTake(10);
        setPage(1);
        setSortBy("TIMESTAMP");
        setSortDirection("DESC");
    }

    async function changeSort(sortByParam: 'TIMESTAMP' | 'CHANGED_BY' | 'COMMENT' | 'NONE') {
        if (sortByParam === 'NONE') return;

        let newSortDirection: "DESC" | "ASC";
        let newSortBy: "TIMESTAMP" | "CHANGED_BY" | "COMMENT";

        if (sortBy === sortByParam) {
            newSortDirection = sortDirection === "ASC" ? "DESC" : "ASC";
            newSortBy = sortBy;
        } else {
            newSortBy = sortByParam as any;
            newSortDirection = "ASC";
        }

        setSortBy(newSortBy);
        setSortDirection(newSortDirection);
        setPage(1);

        setIsLoading(true);
        const auth = backend.withTokenAuthHeader(authentication.token);
        const historyData: CompanyConfigHistory = await backend.internalApi.fetchCompanyConfigurationHistory(
            context.companyId,
            commentQuery,
            take,
            0,
            newSortBy,
            newSortDirection,
            null,
            'CONFIG_ONLY',
            auth
        );
        setHistory(historyData.results);
        setCount(historyData.count);
        context.setHistory(historyData);
        setIsLoading(false);
    }

    function changePage(pageIndex: number) {
        fetchHistory(take, pageIndex + 1).then();
    }

    function changePageSize(newSize: number) {
        fetchHistory(newSize, 1).then();
    }

    function fillDummyEntries(historyArr: CompanyHistory[], totalAmount: number) {
        // Simply return the current page's data
        // ReactTable will handle pagination display
        return historyArr;
    }

    async function openChanges(row: CompanyHistory) {
        const auth = backend.withTokenAuthHeader(authentication.token);
        const response = await backend.internalApi.fetchFollowingCompanyConfigurationHistory(context.companyId, row._id, auth);
        setShowChanges(true);
        setSelectedHistory(row);
        setFollowingConfig(response.followingConfig);
    }


    if (isLoading) {
        return <Loader active>Loading...</Loader>;
    } else if (history == null) {
        return <p>No histories found</p>;
    } else {
        const filledHistory: CompanyHistory[] | null = fillDummyEntries(history, count);
        const columns = [
            {
                id: 'TIMESTAMP',
                Header: 'timestamp',
                width: 150,
                accessor: (d: CompanyHistory) => d.timestamp,
                Cell: (d: any) => <span>{formatDate(d.value)}</span>,
                sortMethod: (a: any, b: any) => {
                    let a1 = new Date(a).getTime();
                    let b1 = new Date(b).getTime();
                    if (a1 < b1) return 1;
                    else if (a1 > b1) return -1;
                    else return 0;
                },
                sortField: 'TIMESTAMP'
            },
            {
                id: 'CHANGED_BY',
                Header: 'changedBy',
                width: 200,
                accessor: (d: CompanyHistory) => d.changedBy,
                sortField: 'CHANGED_BY'
            },
            {
                id: 'WORKTIME',
                Header: 'WorkTime',
                width: 80,
                accessor: (d: CompanyHistory) => d.workTimeInMinutes,
            },
            {
                id: 'TICKET_NRS',
                Header: 'TicketNrs',
                width: 80,
                accessor: (d: CompanyHistory) => displayTicketNrs(d.ticketNrs),
            },
            {
                id: 'COMMENT',
                Header: 'comment',
                accessor: (d: CompanyHistory) => d.changeComment,
                sortField: 'COMMENT'
            },
        ];

        function drawSearchBar() {
            return (
                <div id={"HistoryTable"} style={{width: "100%", display: "flex", flexDirection: "row", alignItems: "center"}}>
                    <Input
                        icon="search"
                        placeholder="Search..."
                        onChange={(e: any) => {
                            setCommentQuery(e.target.value)
                        }}
                        value={commentQuery}
                        style={{flex: 1}}
                        action={
                            <Button
                                icon
                                onClick={() => {
                                    fetchHistory().then();
                                }}
                            >
                                <Icon name="search"/>
                            </Button>
                        }
                    />
                    <Button
                        icon
                        disabled={commentQuery === ""}
                        onClick={() => {
                            setCommentQuery("");
                            fetchHistory(10, 1, "").then();
                        }}
                        style={{marginLeft: 8}}
                    >
                        <Icon name="trash"/>
                    </Button>
                </div>
            );
        }

        return (
            <Segment>
                {drawSearchBar()}
                <ReactTable
                    data={filledHistory}
                    pages={Math.ceil(count / take)}
                    page={page - 1}
                    manual
                    columns={columns}
                    sorted={[
                        {
                            id: 'TIMESTAMP',
                            desc: false
                        }
                    ]}
                    onSortedChange={(newSorted, column, shiftKey) => {
                        changeSort(column.sortField).then();
                    }}
                    defaultPageSize={take}
                    pageSize={take}
                    className="-striped -highlight"
                    loading={isLoading}
                    getTdProps={(state, rowInfo, column, instance) => ({
                        onClick: () => {
                            if (rowInfo) {
                                if (column.id != "TICKET_NRS") {
                                    openChanges(rowInfo.row._original).then();
                                }
                            }
                        }
                    })}
                    style={{cursor: "pointer"}}
                    onPageChange={changePage}
                    onPageSizeChange={changePageSize}
                />

                <HistoryModal
                    isOpen={showChanges}
                    onClose={async (openRollback: boolean, commentDidChange: boolean) => {
                        if (openRollback) {
                            setShowChanges(false);
                            setShowRollBackView(true);
                        } else {
                            setShowChanges(false);
                        }
                        if (commentDidChange) {
                            await fetchHistory();
                        }
                    }}
                    history={selectedHistory}
                    followingConfig={followingConfig}
                />
                <IfBox shouldShow={showRollBackView}>
                    <RollbackConfigModal
                        isOpen={showRollBackView}
                        onClose={(reload) => {
                            setShowRollBackView(false);
                            props.reloadFromContext(reload);
                        }}
                        currentConfig={workingConfig.value}
                        history={selectedHistory}
                    />
                </IfBox>
            </Segment>
        );
    }
}

export function displayTicketNrs(ticketNrs: string[] | undefined): JSX.Element {
    let baseGitlabTicketUrl = "https://gitlab.logenios.com/LOGENIOS/xconvert/-/issues/"
    let links = [];
    let separator = ", "
    if (ticketNrs && ticketNrs.length > 0) {
        let i = 0;
        ticketNrs.forEach(tn => {
            i++;
            if (i === ticketNrs.length) {
                separator = "";
            }
            links.push(<a href={baseGitlabTicketUrl + tn} target='_blank'
                          rel='noopener noreferrer'>#{tn}{separator}</a>)
        })
    }
    return <>{links}</>;
}